import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridToolbarProps,
  GridRenderCellParams,
  GridValueOptionsParams,
} from "@mui/x-data-grid";
import { IAgreementService } from "src/interface/IAgreementService";
import { useQuery } from "@tanstack/react-query";
import {
  API_URL,
  BootstrapTooltip,
  calculateSession,
  remainingSeasonsInPersianYear,
} from "src/utilities";
import { useAuth } from "src/context/auth";
import { IService } from "src/interface/IService";
import { ServiceItemType } from "src/interface/enum/ServiceItemType";
import { IServiceItem } from "src/interface/IServiceItem";
import { IFollowUpGroups } from "src/interface/IFollowUpGroups";
import { IAutoCompleteLabelValue } from "src/interface/enum/GeneralType";
import { FollowUpType } from "src/interface/enum/FollowUpType";
import { FormikProps } from "formik";

interface AgreementServiceProps {
  rows: IAgreementService[];
  setRows: (rows: IAgreementService[]) => void;
  ServicesData: IService[];
  formik: FormikProps<any>;
  existingAgreementServices?: IAgreementService[];
  isEdit?: boolean;
}

export default function AgreementService({
  rows,
  setRows,
  ServicesData,
  formik,
  existingAgreementServices,
  isEdit,
}: AgreementServiceProps) {
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  );

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };
  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows?.filter((row: any) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows?.find((row: any) => row.id === id);
    if (editedRow?.isNew) {
      setRows(rows?.filter((row: any) => row.id !== id)); // حذف ردیف جدید در صورت انصراف
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow: IAgreementService = { ...newRow, isNew: false }; // علامت‌گذاری ردیف به عنوان "نه جدید"

    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));

    return updatedRow;
  };
  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  const { user } = useAuth();

  React.useEffect(() => {
    const sessionCounter = formik.values.sessionCounter ?? 1;

    if (ServicesData && ServicesData.length > 0) {
      if (
        isEdit &&
        existingAgreementServices &&
        existingAgreementServices.length > 0
      ) {
        if (ServicesData && ServicesData.length > 0) {
          setRows(
            ServicesData.sort((a, b) => a.rowId - b.rowId).map(
              (item: IService) => {
                const existingService = existingAgreementServices.find(
                  (row) => row.serviceRef === item.id
                );
                return {
                  id: existingService?.id ?? item.id,
                  agreementRef:
                    existingService?.agreementRef ??
                    rows.find((row) => row.id === item.id)?.agreementRef ??
                    0,
                  serviceRef: item.id,
                  service: item,
                  serviceItemByTypeRef:
                    existingService?.serviceItemByTypeRef ??
                    rows.find((row) => row.id === item.id)
                      ?.serviceItemByTypeRef ??
                    0,
                  serviceItemByType:
                    existingService?.serviceItemByType ?? undefined,
                  serviceItemsByType: item?.serviceItems?.filter(
                    (s) => s.typeItem === ServiceItemType.Type
                  ),
                  serviceItemByVolumeRef:
                    existingService?.serviceItemByVolumeRef ??
                    rows.find((row) => row.id === item.id)
                      ?.serviceItemByVolumeRef ??
                    0,
                  serviceItemByVolume:
                    existingService?.serviceItemByVolume ?? undefined,
                  serviceItemsByVolume: item?.serviceItems?.filter(
                    (s) => s.typeItem === ServiceItemType.Volume
                  ),
                  count:
                    existingService?.count ??
                    calculateSession(sessionCounter, item.followUpType),
                  followUpGroupRef:
                    existingService?.followUpGroupRef ??
                    rows.find((row) => row.id === item.id)?.followUpGroupRef ??
                    0,
                  isFollowUp: existingService?.isFollowUp ?? item.isFollowUp,
                  isPayment: existingService?.isPayment ?? item.isPayment,
                  isApprovedByManager:
                    existingService?.isApprovedByManager ??
                    rows.find((row) => row.id === item.id)
                      ?.isApprovedByManager ??
                    false,
                  desc:
                    existingService?.desc ??
                    rows.find((row) => row.id === item.id)?.desc ??
                    "",
                  isNew: existingService?.isNew ?? true,
                  selected: existingService ? true : false,
                };
              }
            )
          );
        }
      } else {
        setRows(
          ServicesData.sort((a, b) => a.rowId - b.rowId).map(
            (item: IService) => ({
              id: item.id,
              agreementRef:
                rows.find((row) => row.id === item.id)?.agreementRef ?? 0, // شناسه قرارداد
              serviceRef: item.id, // خدمت انتخابی
              service: item,
              serviceItemByTypeRef:
                rows.find((row) => row.id === item.id)?.serviceItemByTypeRef ??
                0, // نوع انتخابی خدمت
              serviceItemsByType: item?.serviceItems?.filter(
                (s) => s.typeItem === ServiceItemType.Type
              ),
              serviceItemByVolumeRef:
                rows.find((row) => row.id === item.id)
                  ?.serviceItemByVolumeRef ?? 0, // حجم انتخابی خدمت
              serviceItemsByVolume: item?.serviceItems?.filter(
                (s) => s.typeItem === ServiceItemType.Volume
              ),
              count: calculateSession(sessionCounter, item.followUpType),
              followUpGroupRef:
                rows.find((row) => row.id === item.id)?.followUpGroupRef ?? 0,
              isFollowUp: item.isFollowUp, // نیاز به پیگیری
              isPayment: item.isPayment, // نیاز به پرداخت
              isApprovedByManager:
                rows.find((row) => row.id === item.id)?.isApprovedByManager ??
                false, // نیاز به تایید مدیر
              desc: rows.find((row) => row.id === item.id)?.desc ?? "",
              isNew: true,
              selected:
                rows.find((row) => row.id === item.id)?.selected ?? false,
            })
          )
        );
      }
      setRowModesModel((oldModel) => ({
        ...oldModel,
        ...ServicesData.reduce((acc: any, item: any) => {
          acc[item.id] = { mode: GridRowModes.Edit, fieldToFocus: "name" };
          return acc;
        }, {}),
      }));
    }
  }, [ServicesData, formik.values.sessionCounter]);

  const { data: FollowUpGroupsData } = useQuery({
    queryKey: ["FollowUpGroupsData"],
    queryFn: () =>
      fetch(API_URL + "followup-groups", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Aceept: "application/json",
        },
      }).then((res) => res.json()),
  });
  const [getFollowUps, setFollowUps] = React.useState<
    IAutoCompleteLabelValue[]
  >([]);
  React.useEffect(() => {
    if (FollowUpGroupsData) {
      setFollowUps(
        FollowUpGroupsData.map((item: IFollowUpGroups) => ({
          label: item.title,
          value: item.id,
          item: item,
        }))
      );
    }
  }, [FollowUpGroupsData]);

  const columns: GridColDef[] = [
    {
      field: "selected",
      headerName: "انتخاب",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      type: "boolean",
    },
    {
      field: "serviceRef",
      headerName: "خدمت",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,

      sortable: true,
      renderCell: (params: GridRenderCellParams<any, string>) =>
        `${params.row.service?.name}`,
    },

    {
      field: "serviceItemByTypeRef",
      headerName: "نوع",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      type: "singleSelect",

      valueOptions: (params: GridValueOptionsParams<any>) => {
        // اطمینان حاصل کنید که serviceItemsByType موجود است و یک آرایه از اشیاء است
        if (
          params.row.serviceItemsByType &&
          Array.isArray(params.row.serviceItemsByType)
        ) {
          return params.row.serviceItemsByType.map((item: IServiceItem) => ({
            value: item.id, // مقدار انتخابی (برای ذخیره در فیلد)
            label: item.title, // برچسبی که به کاربر نمایش داده می‌شود
          }));
        }
        return []; // در صورت عدم وجود داده، آرایه خالی باز می‌گردد
      },
    },

    {
      field: "serviceItemByVolumeRef",
      headerName: "حجم",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      type: "singleSelect",
      valueOptions: (params: GridValueOptionsParams<any>) => {
        // اطمینان حاصل کنید که serviceItemsByVolume موجود است و یک آرایه از اشیاء است
        if (
          params.row.serviceItemsByVolume &&
          Array.isArray(params.row.serviceItemsByVolume)
        ) {
          return params.row.serviceItemsByVolume.map((item: IServiceItem) => ({
            value: item.id, // مقدار انتخابی (برای ذخیره در فیلد)
            label: item.title, // برچسبی که به کاربر نمایش داده می‌شود
          }));
        }
        return []; // در صورت عدم وجود داده، آرایه خالی باز می‌گردد
      },
    },

    {
      field: "count",
      headerName: "تعداد",
      minWidth: 100,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      renderCell(params) {
        const price =
          (params.row.service?.price ?? 0) *
          (params.row.serviceItemsByType?.find(
            (s: IAgreementService) => s.id === params.row.serviceItemByTypeRef
          )?.factor ?? 0) *
          (params.row.count ?? 0) *
          (params.row.serviceItemsByVolume?.find(
            (s: IAgreementService) => s.id === params.row.serviceItemByVolumeRef
          )?.factor ?? 0);
        return (
          <BootstrapTooltip title={price?.toLocaleString()}>
            <p>{params.value}</p>
          </BootstrapTooltip>
        );
      },
    },
    {
      field: "followUpGroupRef",
      headerName: "گروه پیگیری",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      type: "singleSelect",
      valueOptions: (params) =>
        getFollowUps.filter(
          (f) => f.item?.serviceRef === params.row?.serviceRef
        ),
    },

    {
      field: "isFollowUp",
      headerName: "پیامک پیگیری",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      type: "boolean",
    },
    {
      field: "isPayment",
      headerName: "پیامک پرداخت",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      type: "boolean",
      sortable: true,
    },
    {
      field: "isApprovedByManager",
      headerName: "نیاز به تایید مدیر",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      type: "boolean",
      sortable: true,
    },
    {
      field: "desc",
      headerName: "توضیحات",
      minWidth: 200,
      filterable: true,
      align: "left",
      headerAlign: "left",
      flex: 2,
      editable: true,
      sortable: true,
      type: "string",
    },

    // {
    //   field: "actions",
    //   type: "actions",
    //   headerName: "عملیات",
    //   minWidth: 150,
    //   cellClassName: "actions",
    //   getActions: ({ id }) => {
    //     const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
    //     if (isInEditMode) {
    //       return [
    //         <GridActionsCellItem
    //           icon={<SaveIcon />}
    //           label="Save"
    //           sx={{
    //             color: "primary.main",
    //           }}
    //           onClick={handleSaveClick(id)}
    //         />,
    //         <GridActionsCellItem
    //           icon={<CancelIcon />}
    //           label="انصراف"
    //           className="textPrimary"
    //           onClick={handleCancelClick(id)}
    //           color="inherit"
    //         />,
    //       ];
    //     }

    //     return [
    //       <GridActionsCellItem
    //         icon={<EditIcon />}
    //         label="ویرایش"
    //         className="textPrimary"
    //         onClick={handleEditClick(id)}
    //         color="inherit"
    //       />,
    //       <GridActionsCellItem
    //         icon={<DeleteIcon />}
    //         label="حذف"
    //         onClick={handleDeleteClick(id)}
    //         color="inherit"
    //       />,
    //     ];
    //   },
    // },
  ];

  return (
    <Box>
      <DataGrid
        //@ts-ignore
        rows={rows}
        columns={columns}
        editMode="row"
        //rowModesModel={rowModesModel}
        // onRowModesModelChange={handleRowModesModelChange}
        //onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        //@ts-ignore

        // slotProps={{
        //   //@ts-ignore
        //   toolbar: {
        //     setRows,
        //     setRowModesModel,
        //     rows,
        //   },
        // }}
      />
    </Box>
  );
}
